import { PoolStatus } from 'api/types/httpsTypes/contracts';
import { useQueryParams } from 'modules/routing/useQueryParams';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import * as yup from 'yup';
import { isEmpty, omit } from 'lodash/fp';
import { toQueryObject } from 'modules/routing/query-string';

const PAGE_SIZE = 20;
const schema = {
  limit: yup.number().default(PAGE_SIZE),
  offset: yup.number().default(0),
  status: yup.array(yup.string<PoolStatus>().required()),
  channelId: yup.array(yup.string().required()),
  sort: yup.string().default('-message_date'),
  minLiquidity: yup.number(),
  maxLiquidity: yup.number(),
  minMarketcap: yup.number(),
  maxMarketcap: yup.number(),
};

export function usePageParams() {
  return useQueryParams(schema);
}

export type NewCallsFilterState = yup.TypeFromShape<typeof schema, any>;

export const CACHE_KEY_NEW_CALLS = 'newCallsFilters-v4';

export function useCallsSearchFilters() {
  const [filtersState, setFilters] = usePageParams();
  const setFiltersWithCache = useFiltersWithCache(
    setFilters,
    {
      limit: PAGE_SIZE,
      offset: 0,
      sort: '-message_date',
    },
    (queryObj) => isEmpty(omit(['searchDefaultOpen'], queryObj))
  );

  const goToPage = (page: number) => {
    setFilters({
      ...filtersState,
      offset: (page - 1) * filtersState.limit,
    });
  };

  const setSort = (key: string) => {
    let newSortKey;
    if (filtersState.sort?.includes(key)) {
      if (filtersState.sort?.charAt(0) === '-') {
        newSortKey = key;
      } else {
        newSortKey = `-${key}`;
      }
    }
    setFiltersWithCache({
      ...filtersState,
      offset: 0,
      sort: newSortKey || key,
    });
  };

  return { filtersState, setFilters: setFiltersWithCache, goToPage, setSort };
}

function useFiltersWithCache(
  setFilters: (filters: NewCallsFilterState) => void,
  defaults: Partial<NewCallsFilterState> = {},
  shouldApplyCache: (filters: NewCallsFilterState) => boolean = () => true
) {
  const location = useLocation();

  useEffect(() => {
    if (
      shouldApplyCache(
        toQueryObject(location.search) as unknown as NewCallsFilterState
      )
    ) {
      const fromCache = window.localStorage.getItem(CACHE_KEY_NEW_CALLS);

      setFilters(
        fromCache
          ? { ...JSON.parse(fromCache), limit: PAGE_SIZE, offset: 0 }
          : defaults
      );
    }
  }, [location.search]);

  return function cacheAndSetFilters(filters: NewCallsFilterState) {
    window.localStorage.setItem(CACHE_KEY_NEW_CALLS, JSON.stringify(filters));
    setFilters(filters);
  };
}
